import React from "react";

import { ArrowRight, BriefcaseBusiness } from "lucide-react";

import Button from "../../../../../Components/Button";

export type PortfolioSponsorOnlyVariant = "sidebar" | "panel";

export interface PortfolioSponsorOnlyStateProps {
  variant?: PortfolioSponsorOnlyVariant;
  onSwitchToAny?: () => void;
  className?: string;
  /** Overrides the default headline. */
  title?: string;
  /** Overrides the default supporting sentence. */
  description?: React.ReactNode;
  /** Overrides the sidebar text link / panel button label. */
  actionLabel?: string;
}

const DEFAULT_TITLE = "Portfolio view for Sponsors & Account Team.";
const DEFAULT_DESCRIPTION = (
  <>
    View all accounts in the{" "}
    <span className="font-medium text-slate-700">Any Portfolio</span> tab.
  </>
);

const PortfolioSponsorOnlyState: React.FC<PortfolioSponsorOnlyStateProps> = ({
  variant = "panel",
  onSwitchToAny,
  className = "",
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  actionLabel,
}) => {
  const isSidebar = variant === "sidebar";
  const sidebarActionLabel = actionLabel || "Go to Any Portfolio";
  const panelActionLabel = actionLabel || "Switch to Any Portfolio";

  return (
    <div
      className={`flex min-h-[360px] flex-1 items-center justify-center p-3 sm:p-4 ${className}`}
    >
      <div
        className={`w-full text-center ${
          isSidebar ? "px-2 py-6" : "max-w-[360px] px-4 py-2"
        }`}
      >
        <BriefcaseBusiness
          className={`mx-auto text-slate-400 ${
            isSidebar ? "mb-3 h-8 w-8" : "mb-4 h-9 w-9"
          }`}
          strokeWidth={1.5}
          aria-hidden
        />

        <h3
          className={`mb-2 font-semibold leading-snug text-slate-800 ${
            isSidebar ? "text-[14px]" : "text-base"
          }`}
        >
          {title}
        </h3>

        <p
          className={`mb-0 leading-relaxed text-slate-500 ${
            isSidebar ? "text-[12px]" : "text-sm"
          }`}
        >
          {description}
        </p>

        {onSwitchToAny ? (
          <div className={isSidebar ? "mt-4" : "mt-5"}>
            {isSidebar ? (
              <button
                type="button"
                onClick={onSwitchToAny}
                className="inline-flex items-center gap-1 text-[12px] font-medium text-purple-600 transition-colors hover:text-purple-700"
              >
                {sidebarActionLabel}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </button>
            ) : (
              <div className="flex justify-center">
                <Button variant="primary" onClick={onSwitchToAny}>
                  {panelActionLabel}
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PortfolioSponsorOnlyState;
